import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore';
import { AlertTriangle, BookOpen, CheckCircle2, Loader2, RefreshCw, Save, Search, Users } from 'lucide-react';
import { db } from '../../firebase/config';
import { getConfiguredSubjects, getPerformanceRemark } from '../../utils/semesterConfig';

const EXAMS = [
  { key: 'iat1', legacyCollection: 'iaTest1', label: 'IAT-1', maxField: 'iat1Max', marksField: 'iat1Marks', remarkField: 'iat1Remark' },
  { key: 'iat2', legacyCollection: 'iaTest2', label: 'IAT-2', maxField: 'iat2Max', marksField: 'iat2Marks', remarkField: 'iat2Remark' },
  { key: 'ese', legacyCollection: '', label: 'ESE', maxField: 'eseMax', marksField: 'eseMarks', remarkField: 'eseRemark' }
];

const cleanNumberInput = (value) => {
  if (value === '') return '';
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const [first, ...rest] = cleaned.split('.');
  return rest.length ? `${first}.${rest.join('')}` : cleaned;
};

const clampMarksInput = (value, maxMarks) => {
  const cleaned = cleanNumberInput(value);
  if (cleaned === '') return '';

  const numeric = Number(cleaned);
  const maximum = Number(maxMarks);
  if (!Number.isFinite(numeric)) return '';
  if (Number.isFinite(maximum) && maximum > 0 && numeric > maximum) {
    return String(maximum);
  }

  return cleaned;
};

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const studentName = (student) => (
  `${student.firstName || ''} ${student.surname || ''}`.trim() ||
  student.name ||
  student.rollNumber ||
  ''
);

const marksDocId = (rollNumber, semester, subjectId) => (
  `${rollNumber}_semester${semester}_${subjectId}`.replace(/[^a-z0-9_-]/gi, '_')
);

const getExamMarks = (record, examKey) => (
  record?.[examKey]?.marks ?? record?.[`${examKey}Marks`] ?? ''
);

const getExamRemark = (record, examKey) => (
  record?.[examKey]?.remark ?? record?.[`${examKey}Remark`] ?? ''
);

const getExamMax = (subject, exam) => numberOrZero(subject?.[exam.maxField] || subject?.iatMax);

const subjectWithCourseDivision = (subject, teachingDivision) => ({
  ...subject,
  teachingDivision: teachingDivision || subject.teachingDivision || ''
});

const facultyMatchesSubject = (subject, identities) => {
  const email = normalizeText(subject.courseInchargeEmail);
  const name = normalizeText(subject.courseInchargeName);
  return Boolean((email && identities.has(email)) || (name && identities.has(name)));
};

const makeRow = (student, record, subject) => {
  const rawIat1Marks = getExamMarks(record, 'iat1');
  const rawIat2Marks = getExamMarks(record, 'iat2');
  const rawEseMarks = getExamMarks(record, 'ese');
  const iat1Marks = clampMarksInput(rawIat1Marks, getExamMax(subject, EXAMS[0]));
  const iat2Marks = clampMarksInput(rawIat2Marks, getExamMax(subject, EXAMS[1]));
  const eseMarks = clampMarksInput(rawEseMarks, getExamMax(subject, EXAMS[2]));
  const wasClamped = (
    (rawIat1Marks !== '' && String(rawIat1Marks) !== String(iat1Marks)) ||
    (rawIat2Marks !== '' && String(rawIat2Marks) !== String(iat2Marks)) ||
    (rawEseMarks !== '' && String(rawEseMarks) !== String(eseMarks))
  );

  return {
    rollNumber: student.rollNumber,
    name: studentName(student),
    division: student.division || '',
    batch: student.batch || '',
    iat1Marks,
    iat1Remark: getPerformanceRemark(iat1Marks, getExamMax(subject, EXAMS[0])) || getExamRemark(record, 'iat1'),
    iat2Marks,
    iat2Remark: getPerformanceRemark(iat2Marks, getExamMax(subject, EXAMS[1])) || getExamRemark(record, 'iat2'),
    eseMarks,
    eseRemark: getPerformanceRemark(eseMarks, getExamMax(subject, EXAMS[2])) || getExamRemark(record, 'ese'),
    dirty: wasClamped
  };
};

const buildMarkPayload = ({ row, semester, subject, userEmail, userName }) => ({
  rollNumber: row.rollNumber,
  studentName: row.name,
  division: row.division,
  batch: row.batch,
  semester: Number(semester),
  subjectId: subject.id,
  subjectCode: subject.code || '',
  subjectName: subject.name || '',
  iat1Max: numberOrZero(subject.iat1Max || subject.iatMax),
  iat2Max: numberOrZero(subject.iat2Max || subject.iatMax),
  eseMax: numberOrZero(subject.eseMax),
  teachingDivision: subject.teachingDivision || '',
  courseInchargeEmail: subject.courseInchargeEmail || userEmail || '',
  courseInchargeName: subject.courseInchargeName || userName || '',
  iat1: {
    marks: row.iat1Marks === '' ? '' : Number(row.iat1Marks),
    remark: getPerformanceRemark(row.iat1Marks, numberOrZero(subject.iat1Max || subject.iatMax))
  },
  iat2: {
    marks: row.iat2Marks === '' ? '' : Number(row.iat2Marks),
    remark: getPerformanceRemark(row.iat2Marks, numberOrZero(subject.iat2Max || subject.iatMax))
  },
  ese: {
    marks: row.eseMarks === '' ? '' : Number(row.eseMarks),
    remark: getPerformanceRemark(row.eseMarks, numberOrZero(subject.eseMax))
  },
  updatedAt: serverTimestamp(),
  updatedBy: userName || userEmail || 'Course Incharge'
});

const buildLegacyExamRecord = ({ rollNumber, studentName: name, semester, subjects, marksMap, currentPayload, currentSubject, exam }) => {
  const subjectMarks = {};
  let total = 0;
  let maxMarks = 0;

  subjects.forEach((subject) => {
    const id = marksDocId(rollNumber, semester, subject.id);
    const record = subject.id === currentSubject.id ? currentPayload : marksMap[id];
    const marks = record?.[exam.key]?.marks;
    const hasMarks = marks !== '' && marks !== undefined && marks !== null;

    if (hasMarks) {
      const numericMarks = numberOrZero(marks);
      subjectMarks[subject.id] = numericMarks;
      total += numericMarks;
    }

    maxMarks += numberOrZero(subject[exam.maxField] || subject.iatMax);
  });

  return {
    rollNumber,
    studentName: name,
    semester: Number(semester),
    subjects: subjectMarks,
    total,
    maxMarks,
    percentage: maxMarks ? Number(((total / maxMarks) * 100).toFixed(2)) : 0,
    updatedAt: serverTimestamp()
  };
};

const CourseMarksEntryWidget = ({
  userEmail,
  userName,
  userId,
  username,
  assignedDivision,
  courseAssignment
}) => {
  const [semester, setSemester] = useState(String(courseAssignment?.semester || '1'));
  const [subjects, setSubjects] = useState([]);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [students, setStudents] = useState([]);
  const [marksMap, setMarksMap] = useState({});
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const marksInputRefs = useRef({});

  const identities = useMemo(() => new Set([
    userEmail,
    userName,
    userId,
    username
  ].map(normalizeText).filter(Boolean)), [userEmail, userId, userName, username]);
  const lockedToCourse = Boolean(courseAssignment?.subjectId);
  const activeSemester = String(courseAssignment?.semester || semester);
  const courseAssignmentSubjectId = courseAssignment?.subjectId || '';
  const courseAssignmentDivision = courseAssignment?.teachingDivision || courseAssignment?.division || '';

  const selectedSubject = useMemo(() => (
    assignedSubjects.find((subject) => subject.id === selectedSubjectId) || assignedSubjects[0] || null
  ), [assignedSubjects, selectedSubjectId]);

  useEffect(() => {
    if (!lockedToCourse) return;
    setSemester(String(courseAssignment.semester || '1'));
    setSelectedSubjectId(courseAssignment.subjectId || '');
  }, [courseAssignment?.semester, courseAssignment?.subjectId, lockedToCourse]);

  const rebuildRows = useCallback((studentList, markRecords, subject) => {
    if (!subject) {
      setRows([]);
      return;
    }

    const divisionScope = subject.teachingDivision || assignedDivision || '';
    const scopedStudents = divisionScope
      ? studentList.filter((student) => student.division === divisionScope)
      : studentList;

    setRows(scopedStudents.map((student) => makeRow(
      student,
      markRecords[marksDocId(student.rollNumber, activeSemester, subject.id)],
      subject
    )));
  }, [activeSemester, assignedDivision]);

  const loadMarksSheet = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const [subjectsDoc, studentsSnap, marksSnap] = await Promise.all([
        getDoc(doc(db, 'semesterSubjects', `semester${activeSemester}`)),
        getDocs(query(collection(db, 'students'), orderBy('rollNumber', 'asc'))),
        getDocs(collection(db, 'courseMarks'))
      ]);

      const configuredSubjects = getConfiguredSubjects(
        subjectsDoc.exists() ? subjectsDoc.data() : null,
        Number(activeSemester)
      );
      const nextAssignedSubjects = lockedToCourse
        ? configuredSubjects
            .filter((subject) => subject.id === courseAssignmentSubjectId)
            .map((subject) => subjectWithCourseDivision(subject, courseAssignmentDivision))
        : configuredSubjects.filter((subject) => facultyMatchesSubject(subject, identities));
      const preferredSubjectId = lockedToCourse ? courseAssignmentSubjectId : selectedSubjectId;
      const nextSubject = nextAssignedSubjects.find((subject) => subject.id === preferredSubjectId) ||
        nextAssignedSubjects[0] ||
        null;
      const nextStudents = studentsSnap.docs.map((studentDoc) => studentDoc.data());
      const nextMarksMap = {};

      marksSnap.docs.forEach((markDoc) => {
        const mark = markDoc.data();
        if (Number(mark.semester) === Number(activeSemester)) {
          nextMarksMap[markDoc.id] = mark;
        }
      });

      setSubjects(configuredSubjects);
      setAssignedSubjects(nextAssignedSubjects);
      setSelectedSubjectId(nextSubject?.id || '');
      setStudents(nextStudents);
      setMarksMap(nextMarksMap);
      rebuildRows(nextStudents, nextMarksMap, nextSubject);
    } catch (loadError) {
      console.error('Error loading course marks sheet:', loadError);
      setError('Could not load course marks sheet.');
    } finally {
      setLoading(false);
    }
  }, [activeSemester, courseAssignmentDivision, courseAssignmentSubjectId, identities, lockedToCourse, rebuildRows, selectedSubjectId]);

  useEffect(() => {
    loadMarksSheet();
  }, [loadMarksSheet]);

  const changeSubject = (subjectId) => {
    if (lockedToCourse) return;
    const subject = assignedSubjects.find((item) => item.id === subjectId) || null;
    setSelectedSubjectId(subjectId);
    setMessage('');
    setError('');
    rebuildRows(students, marksMap, subject);
  };

  const updateRow = (rollNumber, field, value) => {
    const examForMarks = EXAMS.find((exam) => exam.marksField === field);
    const maxMarks = examForMarks ? getExamMax(selectedSubject, examForMarks) : 0;
    const nextValue = examForMarks ? clampMarksInput(value, maxMarks) : value;
    const generatedRemark = examForMarks
      ? getPerformanceRemark(nextValue, maxMarks)
      : '';

    setRows((currentRows) => currentRows.map((row) => (
      row.rollNumber === rollNumber
        ? {
            ...row,
            [field]: nextValue,
            ...(examForMarks ? { [examForMarks.remarkField]: generatedRemark } : {}),
            dirty: true
          }
        : row
    )));
  };

  const invalidEntries = useMemo(() => {
    if (!selectedSubject) return [];

    return rows.flatMap((row) => EXAMS
      .filter((exam) => {
        const value = row[exam.marksField];
        return value !== '' && numberOrZero(value) > numberOrZero(selectedSubject[exam.maxField] || selectedSubject.iatMax);
      })
      .map((exam) => `${row.rollNumber} ${exam.label}`));
  }, [rows, selectedSubject]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) => (
      row.rollNumber.toLowerCase().includes(needle) ||
      row.name.toLowerCase().includes(needle) ||
      row.division.toLowerCase().includes(needle) ||
      row.batch.toLowerCase().includes(needle)
    ));
  }, [rows, search]);

  const handleMarksKeyDown = (event, rowIndex, marksField) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const nextIndex = event.shiftKey ? rowIndex - 1 : rowIndex + 1;
    const nextRow = filteredRows[nextIndex];

    if (!nextRow) {
      event.currentTarget.blur();
      return;
    }

    const nextInput = marksInputRefs.current[`${nextRow.rollNumber}:${marksField}`];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const dirtyRows = rows.filter((row) => row.dirty);
  const canSave = Boolean(selectedSubject) && dirtyRows.length > 0 && !saving && invalidEntries.length === 0;

  const saveRows = async () => {
    if (!canSave) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      let batch = writeBatch(db);
      let writeCount = 0;
      const nextMarksMap = { ...marksMap };

      const commitIfNeeded = async (force = false) => {
        if (!writeCount) return;
        if (!force && writeCount < 450) return;
        await batch.commit();
        batch = writeBatch(db);
        writeCount = 0;
      };

      for (const row of dirtyRows) {
        const id = marksDocId(row.rollNumber, activeSemester, selectedSubject.id);
        const payload = buildMarkPayload({
          row,
          semester: activeSemester,
          subject: selectedSubject,
          userEmail,
          userName
        });
        nextMarksMap[id] = payload;

        batch.set(doc(db, 'courseMarks', id), payload, { merge: true });
        writeCount += 1;

        for (const exam of EXAMS.filter((item) => item.legacyCollection)) {
          batch.set(doc(db, exam.legacyCollection, row.rollNumber), buildLegacyExamRecord({
            rollNumber: row.rollNumber,
            studentName: row.name,
            semester: activeSemester,
            subjects,
            marksMap: nextMarksMap,
            currentPayload: payload,
            currentSubject: selectedSubject,
            exam
          }), { merge: true });
          writeCount += 1;
        }

        await commitIfNeeded();
      }

      await commitIfNeeded(true);

      setMarksMap(nextMarksMap);
      setRows((currentRows) => currentRows.map((row) => ({ ...row, dirty: false })));
      setMessage(`Saved marks for ${dirtyRows.length} student${dirtyRows.length === 1 ? '' : 's'}.`);
    } catch (saveError) {
      console.error('Error saving course marks:', saveError);
      setError('Could not save course marks. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading course marks sheet
      </div>
    );
  }

  if (!assignedSubjects.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-5 text-sm text-amber-800">
        <div className="flex items-center font-semibold">
          <AlertTriangle className="mr-2 h-4 w-4" />
          No subject is assigned to this faculty account.
        </div>
        <p className="mt-2">
          Ask the HOD to assign this faculty as Course Incharge in Semester Subject Management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Semester</span>
          <select
            value={activeSemester}
            disabled={lockedToCourse}
            onChange={(event) => {
              setSemester(event.target.value);
              setSelectedSubjectId('');
            }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
          >
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </label>

        <label className="lg:col-span-4">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Subject</span>
          <select
            value={selectedSubject?.id || ''}
            disabled={lockedToCourse}
            onChange={(event) => changeSubject(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
          >
            {assignedSubjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.code ? `${subject.code} - ` : ''}{subject.name}{subject.teachingDivision ? ` (Division ${subject.teachingDivision})` : ''}
              </option>
            ))}
          </select>
        </label>

        <div className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Scope</span>
          <div className="flex items-center rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
            <Users className="mr-2 h-4 w-4 text-gray-500" />
            {selectedSubject?.teachingDivision
              ? `Division ${selectedSubject.teachingDivision}`
              : assignedDivision
                ? `Division ${assignedDivision}`
                : 'All students'}
          </div>
        </div>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Roll or name"
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </label>

        <div className="flex items-end gap-2 lg:col-span-2">
          <button
            type="button"
            onClick={loadMarksSheet}
            className="inline-flex flex-1 items-center justify-center rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Load
          </button>
          <button
            type="button"
            onClick={saveRows}
            disabled={!canSave}
            className="inline-flex flex-1 items-center justify-center rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </button>
        </div>
      </div>

      {selectedSubject && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <div className="flex items-center font-semibold">
            <BookOpen className="mr-2 h-4 w-4" />
            {selectedSubject.courseInchargeName || userName || 'Course Incharge'}
          </div>
          <div className="mt-1 text-xs">
            IAT-1 max: {selectedSubject.iat1Max || selectedSubject.iatMax} | IAT-2 max: {selectedSubject.iat2Max || selectedSubject.iatMax} | ESE max: {selectedSubject.eseMax}
          </div>
        </div>
      )}

      {invalidEntries.length > 0 && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {invalidEntries.length} mark entr{invalidEntries.length === 1 ? 'y is' : 'ies are'} above the HOD-set maximum.
        </div>
      )}

      {error && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {error}
        </div>
      )}

      {message && (
        <div className="flex items-center rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-[1320px] w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left">Roll</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Student</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Division</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Batch</th>
              {EXAMS.map((exam) => (
                <React.Fragment key={exam.key}>
                  <th className="border-b border-r border-gray-200 px-3 py-2 text-center">
                    {exam.label}
                  </th>
                  <th className="border-b border-r border-gray-200 px-3 py-2 text-left">
                    {exam.label} Remark
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-3 py-8 text-center text-gray-500">
                  No students found.
                </td>
              </tr>
            ) : (
              filteredRows.map((row, rowIndex) => (
                <tr key={row.rollNumber} className={row.dirty ? 'bg-indigo-50/50' : 'bg-white'}>
                  <td className="sticky left-0 z-10 border-r border-t border-gray-200 bg-inherit px-3 py-2 font-semibold text-gray-900">
                    {row.rollNumber}
                  </td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-800">{row.name}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-600">{row.division || '-'}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-600">{row.batch || '-'}</td>
                  {EXAMS.map((exam) => {
                    const max = getExamMax(selectedSubject, exam);
                    const tooHigh = row[exam.marksField] !== '' && numberOrZero(row[exam.marksField]) > max;
                    const remark = getPerformanceRemark(row[exam.marksField], max);

                    return (
                      <React.Fragment key={exam.key}>
                        <td className={`border-r border-t border-gray-200 px-2 py-1 ${tooHigh ? 'bg-red-50' : ''}`}>
                          <input
                            ref={(input) => {
                              marksInputRefs.current[`${row.rollNumber}:${exam.marksField}`] = input;
                            }}
                            type="number"
                            min="0"
                            max={max || undefined}
                            step="0.01"
                            value={row[exam.marksField]}
                            onChange={(event) => updateRow(row.rollNumber, exam.marksField, event.target.value)}
                            onKeyDown={(event) => handleMarksKeyDown(event, rowIndex, exam.marksField)}
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          <div className="text-center text-[10px] text-gray-400">/{max || '-'}</div>
                        </td>
                        <td className="border-r border-t border-gray-200 px-2 py-1">
                          <input
                            value={row[exam.remarkField] || remark}
                            readOnly
                            placeholder="-"
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-gray-700"
                          />
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourseMarksEntryWidget;
